import { environment } from './../environments/environment';
import { RandomUserResponse } from './shared/interfaces/random-user-name';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ConfigDataService } from './modules/olp-api/services/config-data/config-data.service';
import { AuthSessionService } from './modules/auth/services/auth-session/auth-session.service';
import { FeatureFlagService } from './modules/feature-flags/services/feature-flag/feature-flag.service';
import {
  ActivityStateEnum,
  MessageCodes,
  TriggerActionTypes,
} from 'src/app/shared/enums';
import { MessageService } from './modules/message/services/message/message.service';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ActivityService,
  LoggingService,
  ScrollbarService,
  UserService,
} from './shared/services';
import { KnowledgeBaseService } from './modules/olp-api/services/kb/kb.service';
import { SettingsService } from './shared/services/settings/settings.service';
import { FooterService } from './modules/footer/services/footer.service';
import { PageTransitionService } from './shared/services/page-transitions/page-transitions.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { ConfigurationService } from './modules/configuration/services/configuration/configuration.service';
import { UseAnimationService } from './modules/use-animation/use-animation.service';
import { ReferrerService } from './shared/services/referrer/referrer.service';
import { IProgressNodeData } from './modules/progress-bar/interfaces/progress-node-data.interface';
import { FeatureFlags } from './shared/enums/feature-flags';
import { ClickstreamService } from './modules/olp-api/services/clickstream/clickstream.service';
import { TrackingService } from './modules/tracking/services/tracking.service';
import {
  LoadingScreenService,
  LoadingScreenState,
} from './modules/loading-screen/services/loading-screen.service';
import { ExpandedContentService } from './modules/expandable-content/services/expanded-content.service';
import { SignalRService } from './shared/services/signalr/signalr.service';
import { UrlService } from './shared/services';
import { WA_WINDOW } from '@ng-web-apis/common';
import { AmiraSISService } from './modules/student-inbox-api/services/amira-sis.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { CredentialsRefreshService } from './shared/services/credentials-refresh/credentials-refresh.service';

@Component({
  selector: 'htc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  subscription = new Subscription();
  showFeatureFlagMenu = false;
  useAnimation = true;
  @ViewChild('htcPageContentContainer', { static: true })
  pageContentContainer!: ElementRef;
  ActivityStateEnum = ActivityStateEnum;
  platformSupported = true;
  FeatureFlags = FeatureFlags;
  exitButtonText = 'BUTTONS.EXIT';
  exitButtonIcon = '/assets/button-icons/arrow.svg';
  timeoutFlag = false;
  timeoutTimer!: NodeJS.Timeout;
  animationDelay = 1000;
  expandedContentOpened = false;

  private window = inject(WA_WINDOW);

  constructor(
    public activityService: ActivityService,
    public userService: UserService,
    public footerService: FooterService,
    public pageTransitionService: PageTransitionService,
    public scrollbarService: ScrollbarService,
    public featureFlagService: FeatureFlagService,
    public expandedContentService: ExpandedContentService,
    public authSessionService: AuthSessionService,
    public loadingScreenService: LoadingScreenService,
    private messageService: MessageService,
    private kbService: KnowledgeBaseService,
    private clickstreamService: ClickstreamService,
    private configDataService: ConfigDataService,
    private settingsService: SettingsService,
    private httpClient: HttpClient,
    private router: Router,
    private location: Location,
    private configurationService: ConfigurationService,
    private title: Title,
    private useAnimationService: UseAnimationService,
    private referrerService: ReferrerService,
    private trackingService: TrackingService,
    private signalRService: SignalRService,
    private urlService: UrlService,
    private amiraSISService: AmiraSISService,
    private loggingService: LoggingService,
    private cdr: ChangeDetectorRef,
    private oidcSecurityService: OidcSecurityService,
    private credentialsRefreshService: CredentialsRefreshService
  ) {
    const urlParams = new URLSearchParams(window.location.search);
    const hmhGuid = urlParams.get('hmhGuid'); // If URL has hmhGuid param, set this value in userService for Amira SIS calls
    this.userService.hmhId = hmhGuid || '';
  }

  ngOnInit(): void {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    window.addEventListener('unload', function () {});
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    window.addEventListener('beforeunload', function () {});
    if (
      !this.featureFlagService.isFlagEnabled(FeatureFlags.EARLY_ACCESS) &&
      !this.getWindowHref().includes('auth/logout')
    ) {
      this.loadingScreenService.loadingScreenState.set(
        LoadingScreenState.shown
      );
    }
    this.timeoutFlag = false;
    this.pageTransitionService.setup();

    this.subscription.add(
      this.useAnimationService.useAnimation$.subscribe(useAnim => {
        this.animationDelay = useAnim ? 1750 : 500;
      })
    );

    this.subscription.add(
      this.expandedContentService.isOpen().subscribe(isOpen => {
        this.expandedContentOpened = isOpen;
        this.cdr.markForCheck();
      })
    );

    // AppSync GraphQL queries using IAM/SigV4 auth are broken if the
    // authenticate feature flag is off (no id_token available).
    if (
      !this.featureFlagService.isFlagEnabled(FeatureFlags.AUTHENTICATE) &&
      this.configurationService.usingIamAuth
    ) {
      console.error(
        'Invalid configuration: appSyncIdentityPoolId is set but authenticate feature flag is off'
      );
    }

    if (this.featureFlagService.isFlagEnabled(FeatureFlags.AUTHENTICATE)) {
      this.manageCredentialsRefresh();

      if (this.referrerService.getNavTarget().length === 0) {
        this.referrerService.saveNavTarget();
      }
      this.subscription.add(
        this.authSessionService.checkAuth().subscribe({
          // eslint-disable-next-line complexity
          next: ({ isAuthenticated, userData, accessToken, errorMessage }) => {
            if (!isAuthenticated) {
              if (errorMessage) {
                console.error('error authenticating', errorMessage);
                this.messageService.showMessage(
                  MessageCodes.HTTP_AUTH_REQUIRED
                );
              }
              return;
            }

            this.referrerService.removeNavTarget();
            this.authSessionService.saveRoles(userData);

            // Load SignalR session watch
            const authUrl: string =
              this.configurationService.config.idServerUrl;
            if (
              this.featureFlagService.isFlagEnabled(
                FeatureFlags.SIGNAL_R_SERVICE
              ) &&
              accessToken
            ) {
              this.signalRService
                .connectToServer(`${authUrl}messageHub`)
                .then(() => {
                  this.signalRService.invokeMethod('VerifyToken', accessToken);
                  this.signalRService
                    .subscribeMessage('ReceiveMessage')
                    .subscribe({
                      next: message => {
                        const [, messageCode] = message;
                        if (messageCode == 'LogoutSession') {
                          this.signalRService.unsubscribeServer().then(() => {
                            this.messageService.sendTrigger(
                              TriggerActionTypes.LOGOUT
                            );
                          });
                        } else if (messageCode == 'ClearSession') {
                          this.signalRService.unsubscribeServer().then(() => {
                            this.referrerService.reSaveNavTarget();
                            // Clear the session and reload the page
                            sessionStorage.removeItem(
                              this.configurationService.config.configId
                            );
                            const lastRoutedUrl =
                              this.urlService.getLastRoutedUrl();
                            if (lastRoutedUrl) {
                              this.window.location.href = lastRoutedUrl;
                            } else {
                              this.window.location.reload();
                            }
                          });
                        }
                      },
                    });
                });
            }
            if (this.authSessionService.isTeacher) {
              this.trackingService.push({
                event: 'is_teacher',
                isTeacher: true,
              });
              this.userService.selectedAvatar.next('Amira2'); // Fallback to default avatar
            } else {
              if (
                this.featureFlagService.isFlagEnabled(
                  FeatureFlags.ENABLE_STUDENT_INBOX
                )
              ) {
                // Fetch user selected avatar data for toolbar buttons
                this.amiraSISService
                  .getUserSelectedAvatar(this.userService.hmhId || userData.oid) // pass hmhId if available, otherwise use oid
                  .then(selectedAvatar => {
                    this.userService.selectedAvatar.next(selectedAvatar);
                  })
                  .catch(error => {
                    console.error('Error fetching user selected avatar', error);
                    this.userService.selectedAvatar.next('Amira2'); // Fallback to default avatar
                  });
              } else {
                this.userService.selectedAvatar.next('Amira2'); // Fallback to default avatar
              }
            }

            this.userService.oid$.next(userData.oid);
            this.loggingService.userId.next(userData.oid);
            this.userService.firstName$.next(userData.given_name);
            this.userService.lastName$.next(userData.family_name);
            const userGrade = userData.grade;
            const grade =
              userGrade && !isNaN(Number(userGrade))
                ? parseInt(userGrade, 10)
                : 0;
            if (
              this.featureFlagService.isFlagEnabled(
                FeatureFlags.FORCE_PRE_K2_FLOW
              )
            ) {
              this.userService.grade$.next(0);
            } else if (
              this.featureFlagService.isFlagEnabled(
                FeatureFlags.FORCE_GRADE_3_PLUS_FLOW
              )
            ) {
              this.userService.grade$.next(3);
            } else {
              this.userService.grade$.next(grade);
            }
            if (
              this.featureFlagService.isFlagEnabled(
                FeatureFlags.USE_CLICKSTREAM
              )
            ) {
              this.clickstreamService.clickstream.userId = userData.oid;
            }

            if (this.featureFlagService.isFlagEnabled(FeatureFlags.USE_KB)) {
              this.kbService.userId = this.configDataService.userId =
                userData.oid;
              this.kbService
                .getAll()
                .then(modules => {
                  if (modules) {
                    if (
                      modules.PowerPath_ActivityCustomizations &&
                      modules.PowerPath_ActivityCustomizations
                        .PPM_AVATARS_CustomizationSelected
                    ) {
                      const avatar =
                        modules.PowerPath_ActivityCustomizations
                          .PPM_AVATARS_CustomizationSelected;
                      this.userService.avatar$.next(avatar as number);
                    }

                    if (modules.MainMenu && modules.MainMenu.Stars) {
                      const stars = modules.MainMenu.Stars as number;
                      this.userService.stars$.next(stars);
                    }
                  }
                })
                .catch((error: Response) => {
                  if (error.status === 401) {
                    this.messageService.showMessage(
                      MessageCodes.HTTP_AUTH_REQUIRED
                    );
                  } else if (error.status >= 400) {
                    this.messageService.showMessage(
                      MessageCodes.HTTP_SERVER_INTERNAL
                    );
                  } else {
                    this.messageService.showMessage(
                      MessageCodes.APP_UNKNOWN_ERROR
                    );
                  }
                });

              this.configDataService
                .getModules()
                .then(modules => {
                  const nextStart = modules?.ISIPCalendar?.nextStart;
                  if (nextStart) {
                    this.userService.schoolYear = new Date(nextStart as string);
                    this.clickstreamService.clickstream.schoolYear =
                      this.userService.schoolYear;
                  }
                })
                .catch(err => {
                  console.error('Error configDataService.getModules', err);
                });
            }

            this.settingsService.initSettings();
          },
          error: error => {
            console.error('error authenticating', error);
            this.referrerService.navigateToReferrer();
          },
        })
      );
    } else {
      this.setupRandomUser();
      this.settingsService.initSettings();
    }

    if (this.referrerService.isEducatorReferrer()) {
      this.exitButtonText = 'BUTTONS.CLOSE';
      this.exitButtonIcon = '/assets/button-icons/close.svg';
    }

    this.title.setTitle(this.configurationService.config.pageTitle);

    this.useAnimationService.init();
    this.subscription.add(
      this.useAnimationService.useAnimation$.subscribe(useAnim => {
        this.useAnimation = useAnim;
        this.cdr.markForCheck();
      })
    );
  }

  ngAfterViewInit(): void {
    this.pageTransitionService.target = this.pageContentContainer.nativeElement;
    this.scrollbarService.applyCustomScrollbarStyles();
    this.setupMutationObserver();
    window.addEventListener('resize', () => {
      this.scrollbarService.applyCustomScrollbarStyles();
    });
  }

  private setupMutationObserver(): void {
    const observer = new MutationObserver(() => {
      this.scrollbarService.applyCustomScrollbarStyles();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  getWindowHref(): string {
    return window.location.href;
  }

  /** Set up to keep AWS credentials for IAM auth warm while logged in and drop them on logout. */
  private manageCredentialsRefresh(): void {
    this.subscription.add(
      this.oidcSecurityService.isAuthenticated$.subscribe(
        ({ isAuthenticated }) => {
          if (isAuthenticated && this.configurationService.usingIamAuth) {
            void this.credentialsRefreshService.start();
          } else if (!isAuthenticated) {
            this.credentialsRefreshService.stop();
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.pageTransitionService.cleanup();
    this.useAnimationService.onDestroy();
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
    }
  }

  @HostListener('contextmenu', ['$event'])
  onRightClick(event: Event): void {
    if (environment.production) {
      event.preventDefault();
    }
  }

  @HostListener('window:popstate')
  hideBeforeBackButton(): void {
    this.messageService.clearMessages();
    this.activityService.exitActivity();
    this.footerService.show = false;
  }

  private setupRandomUser(): void {
    this.userService.avatar$.next(Math.floor(Math.random() * 35) + 1);
    if (this.featureFlagService.isFlagEnabled(FeatureFlags.FORCE_PRE_K2_FLOW)) {
      this.userService.grade$.next(0);
    } else if (
      this.featureFlagService.isFlagEnabled(
        FeatureFlags.FORCE_GRADE_3_PLUS_FLOW
      )
    ) {
      this.userService.grade$.next(3);
    } else {
      this.userService.grade$.next(4); // Default to 4th grade as before.
    }
    this.userService.selectedAvatar.next('Amira2');
    this.httpClient
      .get<RandomUserResponse>('https://randomuser.me/api/?inc=name')
      .subscribe(res => {
        this.userService.firstName$.next(res.results[0].name.first);
        this.userService.lastName$.next(res.results[0].name.last);
      });
  }

  isProd(): boolean {
    return environment.production;
  }

  onProgressNodeClick(node: IProgressNodeData): void {
    if (this.progressBarEnabled && !this.timeoutFlag) {
      this.timeoutFlag = true;
      this.activityService.goToProgressNodeStep(node);
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer);
      }
      this.timeoutTimer = setTimeout(() => {
        this.timeoutFlag = false;
      }, this.animationDelay);
    }
  }

  get progressBarEnabled(): boolean {
    return (
      this.authSessionService.isTeacher ||
      this.featureFlagService.isFlagEnabled(FeatureFlags.AUTO_ENABLE_NEXT)
    );
  }
}
